import { useState, useRef, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

const MODELS = [
  { id: 'ericoder',           label: 'ericoder',           desc: 'artisan_mistral · Coder qwen3-30b', color: '#a78bfa' },
  { id: 'ericoder-fast',      label: 'ericoder-fast',      desc: 'gateway_moe · Reviewer gemma-4-26b',        color: '#22d3ee' },
  { id: 'ericoder-architect', label: 'ericoder-architect', desc: 'architect_llama · Architect gpt-oss-120b',     color: '#f59e0b' },
  { id: 'ericoder-reasoner',  label: 'ericoder-reasoner',  desc: 'reasoner_r1 · Tester gemma-4-26b',       color: '#10b981' },
  { id: 'ericoder-scout',     label: 'ericoder-scout',     desc: 'scout_glm · Scout Granite-4.0',            color: '#f87171' },
];

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  meta?: { provider?: string; model?: string; latency?: number; tokens?: number };
}

function getToken(): string | null {
  return localStorage.getItem('ericoder_token');
}

export function TestPage() {
  const [model, setModel] = useState(MODELS[0]!.id);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [streamText, setStreamText] = useState('');
  const [useStream, setUseStream] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [showSystem, setShowSystem] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const selectedModel = MODELS.find(m => m.id === model) ?? MODELS[0]!;

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamText]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg: Message = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setError('');
    setLoading(true);
    setStreamText('');

    const token = getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const body: any = {
      model,
      messages: [
        ...(systemPrompt ? [{ role: 'system', content: systemPrompt }] : []),
        ...newMessages.map(m => ({ role: m.role, content: m.content })),
      ],
      stream: useStream,
    };

    try {
      const res = await fetch(`${API_BASE}/v1/chat/completions`, { method: 'POST', headers, body: JSON.stringify(body) });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
        throw new Error(errData.error ?? `HTTP ${res.status}`);
      }

      if (useStream) {
        // SSE streaming
        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';
        let meta: Message['meta'] = {};

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const chunk = decoder.decode(value);
          for (const line of chunk.split('\n')) {
            if (!line.startsWith('data: ')) continue;
            const data = line.slice(6).trim();
            if (data === '[DONE]') continue;
            try {
              const parsed = JSON.parse(data);
              const delta = parsed?.choices?.[0]?.delta?.content ?? '';
              accumulated += delta;
              setStreamText(accumulated);
              // Capture ericoder meta from last chunk
              if (parsed._ericoder) meta = {
                provider: parsed._ericoder.provider_used,
                model: parsed._ericoder.model_used,
                latency: parsed._ericoder.latency_ms,
              };
            } catch { /* ignore */ }
          }
        }

        setMessages(prev => [...prev, { role: 'assistant', content: accumulated, meta }]);
        setStreamText('');
      } else {
        // Non-streaming
        const data = await res.json() as any;
        const content = data?.choices?.[0]?.message?.content ?? '';
        const meta: Message['meta'] = {
          provider: data?._ericoder?.provider_used,
          model: data?._ericoder?.model_used,
          latency: data?._ericoder?.latency_ms,
          tokens: data?.usage?.total_tokens,
        };
        setMessages(prev => [...prev, { role: 'assistant', content, meta }]);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const clear = () => {
    setMessages([]);
    setStreamText('');
    setError('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--header-h) - 64px)', gap: '16px' }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div>
          <h1 className="page-title">Live Test</h1>
          <p className="page-subtitle">Test AI nodes directly via OpenAI-compatible endpoint</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button
            className="btn btn--secondary btn--sm"
            onClick={() => setShowSystem(s => !s)}
            style={{ borderColor: showSystem ? 'var(--accent-primary)' : undefined }}
          >
            {showSystem ? '▲' : '▼'} System Prompt
          </button>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={useStream} onChange={e => setUseStream(e.target.checked)} style={{ accentColor: 'var(--accent-primary)' }} />
            Stream
          </label>
          <button className="btn btn--secondary btn--sm" onClick={clear}>Clear</button>
        </div>
      </div>

      {/* Model selector */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {MODELS.map(m => (
          <button
            key={m.id}
            onClick={() => setModel(m.id)}
            style={{
              padding: '6px 12px',
              borderRadius: '999px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              border: `1px solid ${model === m.id ? m.color : 'var(--border)'}`,
              background: model === m.id ? `${m.color}20` : 'var(--bg-glass)',
              color: model === m.id ? m.color : 'var(--text-secondary)',
              fontFamily: 'var(--font-mono)',
              transition: 'all var(--transition)',
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Model info strip */}
      <div style={{
        padding: '8px 14px',
        background: 'var(--bg-elevated)',
        borderRadius: '8px',
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        fontSize: '11px',
        color: 'var(--text-muted)',
      }}>
        <span style={{ color: selectedModel.color, fontWeight: 600 }}>{selectedModel.id}</span>
        <span>→ {selectedModel.desc}</span>
        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)' }}>
          {API_BASE}/v1/chat/completions
        </span>
      </div>

      {/* System prompt (collapsible) */}
      {showSystem && (
        <div>
          <label className="label">System Prompt (optional)</label>
          <textarea
            className="textarea"
            rows={3}
            value={systemPrompt}
            onChange={e => setSystemPrompt(e.target.value)}
            placeholder="You are a helpful assistant..."
            style={{ minHeight: '80px', resize: 'vertical' }}
          />
        </div>
      )}

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        background: 'var(--bg-glass)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        minHeight: 0,
      }}>
        {messages.length === 0 && !loading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '12px', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '40px', opacity: 0.3 }}>⬡</div>
            <div style={{ fontSize: '13px' }}>Send a message to test <strong style={{ color: selectedModel.color }}>{selectedModel.id}</strong></div>
            <div style={{ fontSize: '11px', opacity: 0.6 }}>Press Enter to send, Shift+Enter for newline</div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '85%',
              padding: '10px 14px',
              borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
              background: msg.role === 'user'
                ? 'linear-gradient(135deg, #7c3aed, #06b6d4)'
                : 'var(--bg-elevated)',
              border: msg.role === 'assistant' ? '1px solid var(--border)' : 'none',
              fontSize: '13px',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {msg.content || <em style={{ opacity: 0.4 }}>(empty response)</em>}
            </div>
            {msg.meta && (
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', display: 'flex', gap: '8px', padding: '0 4px' }}>
                {msg.meta.provider && <span>{msg.meta.provider}</span>}
                {msg.meta.model && <span style={{ color: selectedModel.color }}>{msg.meta.model?.split('/').pop()}</span>}
                {msg.meta.latency && <span>{msg.meta.latency}ms</span>}
                {msg.meta.tokens && <span>{msg.meta.tokens} tokens</span>}
              </div>
            )}
          </div>
        ))}

        {/* Streaming text in progress */}
        {streamText && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
            <div style={{
              maxWidth: '85%',
              padding: '10px 14px',
              borderRadius: '14px 14px 14px 4px',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-accent)',
              fontSize: '13px',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              {streamText}
              <span style={{ display: 'inline-block', width: '2px', height: '14px', background: 'var(--accent-primary)', marginLeft: '2px', verticalAlign: 'text-bottom', animation: 'blink 1s step-end infinite' }} />
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {loading && !streamText && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
            <div className="spinner" style={{ width: 14, height: 14 }} />
            Thinking...
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: '10px 14px',
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: '8px',
            color: '#f87171',
            fontSize: '12px',
          }}>
            ⚠ {error}
          </div>
        )}

        <div ref={endRef} />
      </div>

      {/* Input */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
        <textarea
          ref={textareaRef}
          className="textarea"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder={`Message ${selectedModel.id}... (Enter to send)`}
          rows={1}
          disabled={loading}
          style={{
            flex: 1,
            minHeight: '44px',
            maxHeight: '200px',
            resize: 'none',
            fontFamily: 'var(--font-sans)',
            fontSize: '13px',
            lineHeight: 1.5,
            padding: '10px 14px',
          }}
          onInput={e => {
            const el = e.currentTarget;
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 200) + 'px';
          }}
        />
        <button
          className="btn btn--primary"
          onClick={send}
          disabled={loading || !input.trim()}
          style={{ height: '44px', minWidth: '80px', justifyContent: 'center' }}
        >
          {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Send ↵'}
        </button>
      </div>
    </div>
  );
}
